import {
  CardHeader as MuiCardHeader,
  CardHeaderPropsWithComponent as MuiCardHeaderPropsWithComponent,
  CardHeaderTypeMap as MuiCardHeaderTypeMap,
  paperClasses,
  useThemeProps,
  Theme,
  mergeSlotProps,
} from '@mui/material';
import { SxProps } from '@mui/system';
import * as React from 'react';
import {
  cardContentHorizontalThemeSpacing,
  cardContentVerticalThemeSpacing,
} from 'src/components/CardContent/cardContentStyleOverrides';
import type { TypographyProps } from 'src/components/Typography/Typography';
import { getToken } from '@workiva/unify-tokens';
import { OverrideProps } from '@mui/material/OverridableComponent';
import {
  ExtendTypeMap,
  forwardRefToOverridableComponent,
} from 'src/utils/forwardRefToOverridableComponent';

type CardHeaderOwnProps = MuiCardHeaderPropsWithComponent<React.ElementType> & {
  showDividers?: boolean;
};

export type CardHeaderTypeMap<
  AdditionalProps = unknown,
  RootComponent extends React.ElementType = MuiCardHeaderTypeMap['defaultComponent']
> = ExtendTypeMap<
  MuiCardHeaderTypeMap,
  AdditionalProps & CardHeaderOwnProps,
  RootComponent
>;

export type CardHeaderProps<
  RootComponent extends React.ElementType = MuiCardHeaderTypeMap['defaultComponent'],
  AdditionalProps = unknown
> = OverrideProps<
  CardHeaderTypeMap<AdditionalProps, RootComponent>,
  RootComponent
>;

export const CardHeader = forwardRefToOverridableComponent<
  CardHeaderTypeMap,
  CardHeaderProps
>(function UnifyCardHeader(inProps, ref) {
  const props = useThemeProps({ props: inProps, name: 'MuiCardHeader' });
  const {
    showDividers,
    titleTypographyProps: inTitleTypographyProps,
    subheaderTypographyProps: inSubheaderTypographyProps,
    children,
    sx: inSx = {},
    slotProps,
    ...rest
  } = props;

  const unifyTitleProps: Partial<TypographyProps> = {
    // MUI defaults this to body2 when the avatar is present and an h5 otherwise. We explicitly want this to be h5
    // and bold by default.
    variant: 'h5',
    // There is some odd line height issues when there are multiple lines of text and an avatar.
    fontWeight: 700,
    ...inTitleTypographyProps,
  };

  // Override MUI's body2 / body1 styling and propagate custom styling.
  const unifySubheaderProps: Partial<TypographyProps> = {
    variant: 'subtitle2',
    ...inSubheaderTypographyProps,
  };

  const borderStyles: Partial<SxProps<Theme>> = {
    borderBottom: `1px solid ${getToken('Core/Layer/layer-divider')}`,
  };

  // Add the conditional & default outline dividers.
  const sx: SxProps<Theme> = {
    padding: (theme) =>
      theme.spacing(
        cardContentVerticalThemeSpacing,
        cardContentHorizontalThemeSpacing
      ),
    // Skip applying the divider if the flag is unset.
    ...(showDividers !== false && {
      // Apply borders in an outlined parent by default.
      [`.${paperClasses.outlined} &`]: borderStyles,
    }),
    // Force apply border if set.
    ...(showDividers && borderStyles),
    // Propagate the custom styles.
    ...inSx,
  };

  return (
    <MuiCardHeader
      sx={sx}
      ref={ref}
      {...rest}
      slotProps={{
        ...slotProps,
        title: mergeSlotProps(slotProps?.title, unifyTitleProps),
        subheader: mergeSlotProps(slotProps?.subheader, unifySubheaderProps),
      }}
    >
      {children}
    </MuiCardHeader>
  );
});

export default CardHeader;
